import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { useAuth } from './auth';

/**
 * Wishlist context — `lista de deseos`. Like favorites, but each entry can
 * carry a personal note and a priority (1 high, 2 mid, 3 low). Stored in
 * `db.wishlist` server-side; the frontend mirrors only the set of product
 * ids for fast toggle and a full entries list for the dedicated page.
 */
const WishCtx = createContext(null);

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user || user.role !== 'buyer') { setIds(new Set()); return; }
    try {
      const { data } = await api.get('/wishlist');
      setIds(new Set(data.entries.map((e) => e.product_id)));
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (product_id, opts = {}) => {
    if (!user) return false;
    if (ids.has(product_id)) {
      await api.delete(`/wishlist/${product_id}`);
      setIds((s) => { const n = new Set(s); n.delete(product_id); return n; });
      return false;
    }
    await api.post('/wishlist', { product_id, note: opts.note || null, priority: opts.priority || 2 });
    setIds((s) => new Set(s).add(product_id));
    return true;
  };

  return (
    <WishCtx.Provider value={{ ids, toggle, refresh, isOnWishlist: (id) => ids.has(id) }}>
      {children}
    </WishCtx.Provider>
  );
};

export const useWishlist = () => useContext(WishCtx);
